import type { ElementType } from "react";
import { IconChevronRight } from "@tabler/icons-react";
import { Button as BaseButton } from "@base-ui/react/button";
import styles from "./CardAction.module.css";
import { type IconColor } from "./IconColors";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import FeaturedIcon from "./FeaturedIcon";
import Text from "./Text";
import Card from "./Card";
import Tooltip from "./Tooltip";
import useCanWrite from "../../hooks/use-can-write";
import { READ_ONLY_MESSAGE } from "../../utils/permissions";

interface CardActionProps {
  icon?: ElementType;
  iconColor?: IconColor;
  title: string;
  description?: string;
  onClick?: () => void;
  className?: string;
  requiresWrite?: boolean;
  profileId?: number | null;
  disabled?: boolean;
  disabledTooltip?: string;
}

export default function CardAction({
  icon: Icon,
  iconColor = "info",
  title,
  description,
  onClick,
  className,
  requiresWrite,
  profileId,
  disabled,
  disabledTooltip,
}: CardActionProps) {
  const writable = useCanWrite(profileId);
  const blocked = requiresWrite === true && !writable;
  const isDisabled = blocked || disabled === true;
  const tooltipContent = blocked
    ? READ_ONLY_MESSAGE
    : disabled === true
    ? disabledTooltip
    : undefined;

  const card = (
    <Card
      as={BaseButton}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      padding="sm"
      className={classnames(styles.card, className)}
    >
      <div>
        <div className={styles.title}>
          {Icon && (
            <FeaturedIcon
              icon={Icon}
              size="sm"
              shape="square"
              color={iconColor}
            />
          )}
          <Text weight="medium">{title}</Text>
        </div>
        {description && (
          <Text variant="deci" className={styles.description}>
            {description}
          </Text>
        )}
      </div>
      <IconChevronRight size={20} className={styles.chevron} />
    </Card>
  );

  if (tooltipContent) {
    return <Tooltip content={tooltipContent}>{card}</Tooltip>;
  }
  return card;
}
